import { observer } from 'mobx-react-lite'
import { Button } from '@/components/Button'
import { TextFieldContainer } from '@/components/inputs/TextFieldContainer'
import { SelectContainer } from '@/components/inputs/SelectContainer'
import { DateFieldContainer } from '@/components/inputs/DateFieldContainer'
import { DEPENDENT_RELATIONSHIPS } from '@/features/jobs/details/tax-type/tax-prep-organizer/data/dependent-relationships'
import type { DependentAccordionViewModel } from './DependentAccordionViewModel'
import { useId } from 'react'
import { FormContainer } from '@/components/FormContainer'
import { FormFieldGroup } from '@/components/Form'
import { masks } from '@/utils/input-mask-util'
import { FormFieldRequiredLabel } from '../../../components/FormFieldRequiredLabel'
import { StepCardListItemContainer } from '../../../components/StepCardListItemContainer'

interface DependentAccordionProps {
  vm: DependentAccordionViewModel
  onRemove?: () => void
  testId?: string
}

export const DependentAccordion = observer(function DependentAccordion({
  vm,
  onRemove,
  testId,
}: DependentAccordionProps) {
  const formId = useId()
  return (
    <StepCardListItemContainer
      textValue="Dependent"
      icon="baby"
      title={vm.displayName}
      subtitle={vm.displaySubtitle}
      extras={
        <Button
          iconLeft="delete"
          variant="text-destructive"
          label="Remove"
          onPress={onRemove}
          testId={testId ? `${testId}-remove-button` : undefined}
        />
      }
      isExpanded={vm.isExpanded}
      onExpandedChange={vm.toggleExpanded}
      testId={testId}
    >
      <FormContainer id={formId}>
        <FormFieldGroup stackWhenCompact>
          <TextFieldContainer
            autoComplete="given-name"
            model={vm}
            field="firstName"
            label={<FormFieldRequiredLabel label="First name" />}
            placeholder="e.g. John"
            required
            testId={testId ? `${testId}-first-name-input` : undefined}
          />
          <TextFieldContainer
            autoComplete="additional-name"
            model={vm}
            field="middleInitial"
            label="M.I."
            placeholder="e.g. R"
            testId={testId ? `${testId}-middle-initial-input` : undefined}
          />
          <TextFieldContainer
            autoComplete="family-name"
            model={vm}
            field="lastName"
            label={<FormFieldRequiredLabel label="Last name" />}
            placeholder="e.g. Doe"
            required
            testId={testId ? `${testId}-last-name-input` : undefined}
          />
        </FormFieldGroup>
        <FormFieldGroup stackWhenCompact>
          <TextFieldContainer
            autoComplete="off"
            model={vm}
            field="tin"
            label={<FormFieldRequiredLabel label="SSN" />}
            placeholder="e.g. 123-45-6789"
            mask={masks.ssnOrItin}
            required
            data-private
            testId={testId ? `${testId}-ssn-input` : undefined}
          />
          <DateFieldContainer
            model={vm}
            field="dateOfBirth"
            label={<FormFieldRequiredLabel label="Date of birth" />}
            placeholder="MM/DD/YYYY"
            required
            data-private
            testId={testId ? `${testId}-date-of-birth-input` : undefined}
          />
          <SelectContainer
            model={vm}
            field="relationship"
            label={<FormFieldRequiredLabel label="Relationship" />}
            items={DEPENDENT_RELATIONSHIPS}
            getKey={([code]) => code}
            getLabel={([, label]) => label}
            required
            testId={testId ? `${testId}-relationship-select` : undefined}
          />
        </FormFieldGroup>
      </FormContainer>
    </StepCardListItemContainer>
  )
})
